import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { PageComponent } from '../../shared/page/page.component';
import { ButtonComponent } from '../../shared/button/button.component';
import { IconComponent } from '../../shared/icon/icon.component';
import { AuthService } from '../../../services/auth.service';
import { OrganizationService } from '../../../services/organization.service';
import { ToastService } from '../../../services/toast.service';
import { OrganizationModel } from '../../../models/organization.model';
import {PageTabDirective} from '../../shared/page/page-tab.directive';
import {ApiKeysComponent} from '../api-keys/api-keys.component';

/**
 * Profile page component showing user information and organization management.
 *
 * Displays:
 * - API keys management
 * - User profile (username, email)
 * - List of organizations (personal first, then teams)
 * - Create organization button
 * - Manage organization buttons for team orgs
 */
@Component({
  selector: 'app-profile',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    PageComponent,
    ButtonComponent,
    IconComponent,
    PageTabDirective,
    ApiKeysComponent
  ],
  templateUrl: './profile.component.html',
  styleUrl: './profile.component.scss',
  host: {
    class: 'app-container'
  }
})
export class ProfileComponent {
  protected readonly authService = inject(AuthService);
  protected readonly orgService = inject(OrganizationService);
  private readonly toastService = inject(ToastService);

  protected readonly showCreateDialog = signal(false);
  protected readonly newOrgName = signal('');
  protected readonly newOrgDescription = signal('');
  protected readonly isCreating = signal(false);

  /**
   * Opens the create organization dialog.
   */
  protected openCreateDialog(): void {
    this.newOrgName.set('');
    this.newOrgDescription.set('');
    this.showCreateDialog.set(true);
  }

  /**
   * Closes the create organization dialog.
   */
  protected closeCreateDialog(): void {
    this.showCreateDialog.set(false);
  }

  /**
   * Creates a new organization.
   */
  protected createOrganization(): void {
    const name = this.newOrgName().trim();
    if (!name) {
      this.toastService.error('Invalid Input', 'Organization name is required');
      return;
    }

    this.isCreating.set(true);
    this.toastService.handleResult(
      'Organization',
      this.orgService.create({
        name,
        description: this.newOrgDescription().trim() || undefined
      }),
      () => {
        this.closeCreateDialog();
        this.isCreating.set(false);
      },
      { successMessage: 'Organization created successfully' }
    );
  }

  /**
   * Gets role display text for an organization.
   */
  protected getRoleText(org: OrganizationModel): string {
    const role = org.currentUserRole;
    return role.charAt(0).toUpperCase() + role.slice(1);
  }

  /**
   * Gets organization type badge text.
   */
  protected getOrgType(org: OrganizationModel): string {
    return org.isPersonal ? 'Personal' : 'Team';
  }
}
